/**
 * Page Sections Hooks
 * React Query hooks for managing page sections in the builder app.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import type { PageSection } from '@limo/database'
import type { SectionSettings } from '@limo/shared'
import { SECTION_REGISTRY } from '@limo/shared'

// =============================================================================
// Section Queries
// =============================================================================

/**
 * Fetch all sections for a page, ordered by display_order
 */
export function usePageSections(pageId: string | undefined) {
  return useQuery({
    queryKey: ['page-sections', pageId],
    queryFn: async (): Promise<PageSection[]> => {
      if (!pageId) throw new Error('Page ID required')

      const { data, error } = await supabase
        .from('page_sections')
        .select('*')
        .eq('page_id', pageId)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!pageId,
  })
}

/**
 * Fetch a single section by ID
 */
export function useSection(sectionId: string | undefined) {
  return useQuery({
    queryKey: ['section', sectionId],
    queryFn: async (): Promise<PageSection> => {
      if (!sectionId) throw new Error('Section ID required')

      const { data, error } = await supabase
        .from('page_sections')
        .select('*')
        .eq('id', sectionId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!sectionId,
  })
}

// =============================================================================
// Section Mutations
// =============================================================================

/**
 * Add a new section to a page
 */
export function useAddSection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      accountId,
      pageId,
      sectionType,
      insertAtIndex,
    }: {
      accountId: string
      pageId: string
      sectionType: string
      insertAtIndex?: number
    }): Promise<PageSection> => {
      // Get the section definition for defaults
      const definition = SECTION_REGISTRY[sectionType]
      if (!definition) {
        throw new Error(`Unknown section type: ${sectionType}`)
      }

      // Get current sections to determine display_order
      const { data: existingSections, error: fetchError } = await supabase
        .from('page_sections')
        .select('id, display_order')
        .eq('page_id', pageId)
        .order('display_order', { ascending: true })

      if (fetchError) throw fetchError

      const sections = existingSections || []
      let newDisplayOrder: number

      if (insertAtIndex !== undefined && insertAtIndex < sections.length) {
        // Insert at specific position
        newDisplayOrder = insertAtIndex

        // Shift existing sections down
        const sectionsToUpdate = sections.filter(
          (s) => s.display_order >= insertAtIndex
        )

        for (const section of sectionsToUpdate) {
          await supabase
            .from('page_sections')
            .update({ display_order: section.display_order + 1 })
            .eq('id', section.id)
        }
      } else {
        // Add at end
        newDisplayOrder =
          sections.length > 0
            ? Math.max(...sections.map((s) => s.display_order)) + 1
            : 0
      }

      // Create the new section
      const { data, error } = await supabase
        .from('page_sections')
        .insert({
          account_id: accountId,
          page_id: pageId,
          section_type: sectionType,
          display_order: newDisplayOrder,
          is_visible: true,
          settings: definition.defaultSettings || {},
          content: definition.defaultContent || {},
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      const definition = SECTION_REGISTRY[section.section_type]
      toast({
        title: 'Section added',
        description: `${definition?.name || section.section_type} has been added to the page.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Error adding section',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Update a section's content
 */
export function useUpdateSectionContent() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
      content,
    }: {
      sectionId: string
      content: Record<string, unknown>
    }): Promise<PageSection> => {
      const { data, error } = await supabase
        .from('page_sections')
        .update({ content })
        .eq('id', sectionId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['section', section.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      toast({
        title: 'Section updated',
        description: 'Your changes have been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error updating section',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Update a section's settings (styling)
 */
export function useUpdateSectionSettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
      settings,
    }: {
      sectionId: string
      settings: SectionSettings
    }): Promise<PageSection> => {
      const { data, error } = await supabase
        .from('page_sections')
        .update({ settings })
        .eq('id', sectionId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['section', section.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      toast({
        title: 'Settings updated',
        description: 'Section styling has been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error updating settings',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Update a section (content and settings together)
 */
export function useUpdateSection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
      content,
      settings,
    }: {
      sectionId: string
      content?: Record<string, unknown>
      settings?: SectionSettings
    }): Promise<PageSection> => {
      const updates: Partial<Pick<PageSection, 'content' | 'settings'>> = {}
      if (content !== undefined) updates.content = content
      if (settings !== undefined) updates.settings = settings

      const { data, error } = await supabase
        .from('page_sections')
        .update(updates)
        .eq('id', sectionId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['section', section.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      toast({
        title: 'Section saved',
        description: 'Your changes have been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error saving section',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Toggle a section's visibility
 */
export function useToggleSectionVisibility() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
      isVisible,
    }: {
      sectionId: string
      isVisible: boolean
    }): Promise<PageSection> => {
      const { data, error } = await supabase
        .from('page_sections')
        .update({ is_visible: isVisible })
        .eq('id', sectionId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['section', section.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      toast({
        title: section.is_visible ? 'Section shown' : 'Section hidden',
        description: section.is_visible
          ? 'The section is now visible on the page.'
          : 'The section is now hidden from the page.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error updating visibility',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Delete a section
 */
export function useDeleteSection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
      pageId,
    }: {
      sectionId: string
      pageId: string
    }): Promise<void> => {
      const { error } = await supabase
        .from('page_sections')
        .delete()
        .eq('id', sectionId)

      if (error) throw error

      // Reorder remaining sections to close gaps
      const { data: remainingSections, error: fetchError } = await supabase
        .from('page_sections')
        .select('id, display_order')
        .eq('page_id', pageId)
        .order('display_order', { ascending: true })

      if (fetchError) throw fetchError

      // Update display_order for all sections to be sequential
      for (let i = 0; i < (remainingSections || []).length; i++) {
        const section = remainingSections![i]
        if (section.display_order !== i) {
          await supabase
            .from('page_sections')
            .update({ display_order: i })
            .eq('id', section.id)
        }
      }
    },
    onSuccess: (_, { pageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['page-sections', pageId],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', pageId],
      })
      toast({
        title: 'Section deleted',
        description: 'The section has been removed from the page.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error deleting section',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Reorder sections on a page
 */
export function useReorderSections() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      pageId,
      sectionIds,
    }: {
      pageId: string
      sectionIds: string[]
    }): Promise<void> => {
      // Update each section's display_order based on its position in the array
      const updates = sectionIds.map((id, index) =>
        supabase
          .from('page_sections')
          .update({ display_order: index })
          .eq('id', id)
      )

      // Execute all updates
      const results = await Promise.all(updates)

      // Check for errors
      const errors = results.filter((r) => r.error)
      if (errors.length > 0) {
        throw errors[0].error
      }
    },
    onSuccess: (_, { pageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['page-sections', pageId],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', pageId],
      })
      // Don't show toast for reorder - it's a quick operation
    },
    onError: (error) => {
      toast({
        title: 'Error reordering sections',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Duplicate a section
 */
export function useDuplicateSection() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      sectionId,
    }: {
      sectionId: string
    }): Promise<PageSection> => {
      // Fetch the original section
      const { data: original, error: fetchError } = await supabase
        .from('page_sections')
        .select('*')
        .eq('id', sectionId)
        .single()

      if (fetchError) throw fetchError

      // Get all sections for this page to determine new display_order
      const { data: allSections, error: listError } = await supabase
        .from('page_sections')
        .select('id, display_order')
        .eq('page_id', original.page_id)
        .order('display_order', { ascending: true })

      if (listError) throw listError

      // Insert after the original (shift others down)
      const insertOrder = original.display_order + 1
      const sectionsToShift = (allSections || []).filter(
        (s) => s.display_order >= insertOrder
      )

      for (const section of sectionsToShift) {
        await supabase
          .from('page_sections')
          .update({ display_order: section.display_order + 1 })
          .eq('id', section.id)
      }

      // Create the duplicate
      const { data: duplicate, error: insertError } = await supabase
        .from('page_sections')
        .insert({
          account_id: original.account_id,
          page_id: original.page_id,
          section_type: original.section_type,
          display_order: insertOrder,
          is_visible: original.is_visible,
          settings: original.settings,
          content: original.content,
        })
        .select()
        .single()

      if (insertError) throw insertError
      return duplicate
    },
    onSuccess: (section) => {
      queryClient.invalidateQueries({
        queryKey: ['page-sections', section.page_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['page', section.page_id],
      })
      const definition = SECTION_REGISTRY[section.section_type]
      toast({
        title: 'Section duplicated',
        description: `A copy of ${definition?.name || section.section_type} has been created.`,
      })
    },
    onError: (error) => {
      toast({
        title: 'Error duplicating section',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    },
  })
}
